import React, { useState, useRef, useEffect } from 'react';
import Microphone from '../images/Microphone.png';

const VoiceSearchModal = ({ isOpen, onClose, onResult }) => {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  const handleMicClick = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.lang = 'en-IN';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      recognition.continuous = false;
      recognitionRef.current = recognition;
      setIsListening(true);

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript.trim();
        
        // Clean up the transcript - remove common words that might interfere
        const cleanedTranscript = transcript
          .replace(/\b(show me|find|search for|i want|give me|display|please|can you|looking for)\b/gi, '')
          .replace(/\s+/g, ' ') // Replace multiple spaces with single space
          .trim();
        
        // Set the cleaned search term
        const finalTranscript = cleanedTranscript || transcript; // Fallback to original if cleaning removes everything
        onResult(finalTranscript);
        handleClose();
      };
      
      recognition.onerror = (event) => {
        setIsListening(false);
        
        // Handle different error types
        if (event.error === 'no-speech') {
          console.log("No speech detected, keeping modal open for retry");
          // Don't close modal, let user try again
        } else if (event.error === 'network') {
          console.log("Network error in voice recognition");
          handleClose();
          alert('Network error. Please check your internet connection and try again.');
        } else if (event.error === 'not-allowed') {
          console.log("Microphone permission denied");
          handleClose();
          alert('Microphone access denied. Please allow microphone permission and try again.');
        } else {
          console.log("Other voice recognition error:", event.error);
          // For other errors, keep modal open for retry
        }
      };
      
      recognition.onend = () => {
        console.log("🛑 Voice recognition ended.");
        setIsListening(false);
        
        // If modal is still open and no result was captured, allow retry
        if (isOpen && !onResult) {
          console.log("Recognition ended without result, ready for retry");
        }
      };
      
      recognition.onstart = () => {
        setIsListening(true);
      };
      
      recognition.onspeechstart = () => {
        console.log("🎤 Speech started");
      };
      
      recognition.onspeechend = () => {
        console.log("🤐 Speech ended");
      };
      
      try {
        recognition.start();
      } catch (error) {
        console.error("Error starting voice recognition:", error);
        setIsListening(false);
        handleClose();
        alert('Error starting voice recognition. Please try again.');
      }
    } else {
      alert('Voice search is not supported in this browser. Please use Chrome or Safari.');
      handleClose();
    }
  };

  const handleClose = () => {
    setIsListening(false);
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }
    onClose();
  };

  // Auto-start listening when modal opens
  useEffect(() => {
    if (isOpen && !isListening) {
      handleMicClick();
    }
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col items-center max-w-xs w-full">
        <div className="mb-3">
          <img 
            src={Microphone} 
            alt="Listening" 
            className={`w-12 h-12 ${isListening ? 'animate-pulse' : ''}`} 
          />
        </div>
        <div className="text-lg font-semibold mb-2 text-gray-900">
          {isListening ? "Listening..." : "Say a product name"}
        </div>
        <div className="text-sm text-gray-500 mb-4 text-center">
          Speak the product you want to search for.
        </div>
        
        {/* Voice status indicator */}
       
        <button
          onClick={handleClose}
          className="mt-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default VoiceSearchModal;
